import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const TermsOfService = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-8 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <h1 className="text-4xl font-serif text-primary mb-2">Terms of Service</h1>
        <p className="text-muted-foreground mb-8">Last updated: March 2026</p>
        
        <div className="space-y-8 text-muted-foreground">
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing or using Étoile ("the Service"), you agree to be bound by these Terms of 
              Service. If you do not agree to these terms, please do not use our Service.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">2. Description of Service</h2>
            <p>
              Étoile is a platform for tracking visits to Michelin-starred restaurants, earning 
              achievement badges, connecting with other food enthusiasts, and receiving personalized 
              restaurant recommendations.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">3. User Accounts</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>You must provide accurate and complete information when creating an account</li>
              <li>You are responsible for maintaining the security of your account credentials</li>
              <li>You must be at least 16 years old to use the Service</li>
              <li>One person may not maintain more than one account</li>
            </ul>
          </section>
          
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">4. User Content</h2>
            <p className="mb-3">When you submit content to Étoile (reviews, photos, posts, comments):</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>You retain ownership of your content</li>
              <li>You grant us a license to use, display, and distribute your content within the Service</li>
              <li>You are responsible for ensuring your content does not violate any laws or third-party rights</li>
              <li>We reserve the right to remove content that violates these terms</li>
            </ul>
          </section>
          
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">5. Acceptable Use</h2>
            <p className="mb-3">You agree NOT to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the Service for any illegal purpose</li>
              <li>Post false, misleading, or defamatory content</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Attempt to gain unauthorized access to the Service</li>
              <li>Use automated systems to access the Service without permission</li>
              <li>Interfere with or disrupt the Service</li>
            </ul>
          </section>
          
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">6. Restaurant Information</h2>
            <p>
              We strive to provide accurate information about Michelin-starred restaurants. However, 
              restaurant details (stars, locations, operating status) may change. We do not guarantee 
              the accuracy of restaurant information and recommend verifying details before visiting.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">7. Badges and Achievements</h2>
            <p>
              Badges and achievements are for entertainment purposes and represent your dining history 
              within our platform. They have no monetary value and cannot be transferred or exchanged.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">8. Restaurant Portal</h2>
            <p>
              If you choose to share your preferences with restaurants, you consent to restaurants 
              viewing this information to enhance your dining experience. You can revoke this consent 
              at any time through your profile settings.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">9. Intellectual Property</h2>
            <p>
              The Service, including its design, features, and content (excluding user-generated content), 
              is owned by Étoile and protected by intellectual property laws. "Michelin" and related 
              marks are trademarks of Michelin. We are not affiliated with Michelin.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">10. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE 
              THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">11. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, ÉTOILE SHALL NOT BE LIABLE FOR ANY INDIRECT, 
              INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES ARISING FROM YOUR USE OF THE SERVICE.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">12. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your account if you violate these terms. 
              You may delete your account at any time. Upon termination, your right to use the 
              Service will cease immediately.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">13. Changes to Terms</h2>
            <p>
              We may modify these terms at any time. Continued use of the Service after changes 
              constitutes acceptance of the modified terms.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">14. Governing Law</h2>
            <p>
              These terms shall be governed by and construed in accordance with applicable laws, 
              without regard to conflict of law principles.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">15. Contact</h2>
            <p>
              For questions about these Terms of Service, contact us at: 
              <span className="text-primary ml-1">legal@etoile-app.com</span>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
